<template>
  <!-- Two panes like Schedule: the sheet's POs on the left, the picked PO's rows on the right. -->
  <v-dialog :model-value="open" :max-width="step === STEP.PREVIEW ? PAIR_WIDTH : UPLOAD_WIDTH" persistent aria-labelledby="import-sheet-title">
    <div ref="root" class="flex flex-col overflow-hidden rounded-xl bg-white shadow-xl" :class="step === STEP.PREVIEW ? 'h-[88vh]' : 'max-h-[88vh]'">
      <template v-if="step === STEP.UPLOAD">
        <header class="border-b border-gray-200 px-5 py-4">
          <h2 id="import-sheet-title" class="m-0 text-lg font-semibold text-gray-900">Import sheet</h2>
          <p class="m-0 mt-1 text-sm text-gray-500">Upload your shipping sheet. You'll see every row before anything is saved.</p>
        </header>
        <v-progress-linear v-if="busy" indeterminate color="primary" height="2" aria-label="Working" />
        <div class="flex flex-col gap-1 px-5 py-4">
          <span class="label">Sheet</span>
          <PackingListInput
            id="import-sheet-file"
            v-model="file"
            accept=".xlsx"
            label="Sheet file"
            hint=".xlsx, the sheet you keep your shipments in."
            :error="error?.field === 'file' ? error.message : null"
          />
        </div>
      </template>

      <div v-else-if="preview" class="flex min-h-0 flex-1">
        <!-- Left: the sheet's POs, filtered by tab. -->
        <div class="flex shrink-0 flex-col border-r border-gray-200" :style="{ width: LIST_WIDTH }">
          <div class="flex flex-col gap-3 border-b border-gray-200 px-4 py-4">
            <h2 id="import-sheet-title" class="m-0 text-lg font-semibold text-gray-900">Import sheet</h2>
            <div class="flex w-full gap-0.5 rounded-lg bg-gray-100 p-1" role="group" aria-label="Show">
              <button
                v-for="item in TABS"
                :key="item.tab"
                type="button"
                class="inline-flex h-7 flex-1 items-center justify-center gap-1 whitespace-nowrap rounded px-1.5 text-xs font-semibold transition-colors duration-150 ease-out"
                :class="item.tab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'"
                :aria-pressed="item.tab === tab"
                @click="selectTab(item.tab)"
              >
                {{ item.label }}
                <span class="font-normal tabular-nums" :class="item.tab === IMPORT_TAB.NEEDS_INPUT && tabCounts[item.tab] > 0 ? 'text-amber-800' : ''">
                  {{ tabCounts[item.tab] }}
                </span>
              </button>
            </div>
          </div>
          <p v-if="preview.rows.length === 0" class="m-0 px-4 py-3 text-sm text-gray-500">The sheet has no rows to import.</p>
          <p v-else-if="listedPos.length === 0" class="m-0 px-4 py-3 text-sm text-gray-500">{{ TABS.find((t) => t.tab === tab)?.empty }}</p>
          <ul v-else class="m-0 min-h-0 flex-1 list-none divide-y divide-gray-100 overflow-y-auto border-b border-gray-100 p-0" aria-label="POs in the sheet">
            <li v-for="s in listedSummaries" :key="s.poNumber">
              <button
                type="button"
                class="flex w-full flex-wrap items-baseline gap-x-2 rounded-none border-l-[3px] py-1.5 pl-2.5 pr-3 text-left transition-colors duration-150 ease-out"
                :class="s.poNumber === selected ? 'bg-blue-50' : 'hover:bg-gray-50'"
                :style="{ borderLeftColor: s.poNumber === selected ? darker(colors.get(s.poNumber)) : colors.get(s.poNumber) }"
                :aria-pressed="s.poNumber === selected"
                @click="selectPo(s.poNumber)"
              >
                <span class="text-sm font-semibold text-gray-900">{{ s.poNumber }}</span>
                <span class="text-xs" :class="summaryTone(s)">{{ s.text }}</span>
                <span class="ml-auto text-xs tabular-nums text-gray-500">{{ rowsLabel(s.rows) }}</span>
              </button>
            </li>
          </ul>
        </div>

        <!-- Right: the picked PO's rows. -->
        <section v-if="selectedSummary" class="flex min-w-0 flex-1 flex-col" aria-labelledby="import-sheet-po">
          <header
            class="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-l-4 border-b-gray-200 py-3 pl-4 pr-5"
            :style="{ borderLeftColor: colors.get(selectedSummary.poNumber) }"
          >
            <h3 id="import-sheet-po" class="m-0 text-base font-semibold text-gray-900">{{ selectedSummary.poNumber }}</h3>
            <span class="text-sm" :class="summaryTone(selectedSummary)">{{ selectedSummary.text }}</span>
            <span v-if="poAlertText" class="text-sm font-semibold text-red-700" role="alert">{{ poAlertText }}</span>
            <span v-if="selectedSummary.done" class="ml-auto" :class="[BADGE, IMPORT_OUTCOME_TONE[IMPORT_OUTCOME.RESOLVED]]">Imported</span>
            <button v-else type="button" class="btn-secondary ml-auto" :disabled="busy" @click="importPo(selectedSummary.poNumber)">Import this PO</button>
          </header>
          <v-progress-linear v-if="busy" indeterminate color="primary" height="2" aria-label="Working" />

          <div class="min-h-0 flex-1 overflow-y-auto bg-gray-50 px-5 py-4">
            <ul class="m-0 list-none overflow-clip rounded-md border border-gray-300 bg-white p-0">
              <li
                v-for="row in selectedRows"
                :key="row.rowKey"
                :data-import-row="row.rowKey"
                class="flex flex-col gap-2 border-t border-gray-100 px-3 py-2 first:border-t-0"
                :class="kept(row) ? '' : 'bg-gray-50'"
              >
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <b v-if="editOf(row).date" class="tabular-nums text-gray-900">{{ isShipped(row) ? 'Shipped ' : '' }}{{ fmtDay(editOf(row).date) }}</b>
                  <b v-else class="text-amber-800">No date</b>
                  <span v-if="row.classification" class="text-gray-500">{{ row.classification }}</span>
                  <span v-if="row.quantity !== null" class="tabular-nums text-gray-500">{{ fmtPcs(row.quantity) }} pcs</span>
                  <template v-if="isShipped(row)">
                    <span v-if="row.forwarder" class="text-gray-500">{{ row.forwarder }}</span>
                    <span v-if="row.hawb" class="font-mono text-gray-500">{{ row.hawb }}</span>
                  </template>
                  <span v-if="noteOf(row)" :id="problemId(row)" class="text-gray-900">{{ noteOf(row) }}</span>
                  <span class="text-[11px] tabular-nums text-gray-400">Row {{ row.rowNumber }}</span>
                  <span v-if="splitNote(row)" class="text-xs text-gray-500">{{ splitNote(row) }}</span>
                  <span v-if="failedReason(results, row.rowKey)" class="font-semibold text-red-700">Failed: {{ failedReason(results, row.rowKey) }}</span>
                  <span v-if="rowAlertText(row)" class="font-semibold text-red-700" role="alert">{{ rowAlertText(row) }}</span>
                  <!-- Status and actions pinned to the right edge, so they line up down the stack whatever the text does. -->
                  <span class="ml-auto flex shrink-0 items-center gap-2">
                    <span v-if="!kept(row)" :class="[BADGE, IMPORT_OUTCOME_TONE[IMPORT_OUTCOME.SKIPPED_UNKNOWN_PO]]">Skipped</span>
                    <span v-else-if="rowDone(row)" :class="[BADGE, IMPORT_OUTCOME_TONE[IMPORT_OUTCOME.RESOLVED]]">Imported</span>
                    <span
                      v-else
                      :class="[BADGE, IMPORT_OUTCOME_TONE[row.outcome], IMPORT_OUTCOME_HELP[row.outcome] ? 'cursor-help underline decoration-dotted underline-offset-2' : '']"
                      :title="IMPORT_OUTCOME_HELP[row.outcome]"
                    >
                      {{ OUTCOME_LABEL[row.outcome] }}
                      <span v-if="IMPORT_OUTCOME_HELP[row.outcome]" class="sr-only">: {{ IMPORT_OUTCOME_HELP[row.outcome] }}</span>
                    </span>
                    <span v-if="row.checkSplit && kept(row) && !rowDone(row)" :class="[BADGE, IMPORT_OUTCOME_TONE[IMPORT_OUTCOME.NEEDS_DATE]]">Check split</span>
                    <template v-if="pushable(row) && !rowDone(row)">
                      <button v-if="kept(row)" type="button" class="ml-1 text-sm font-semibold text-blue-700 hover:underline" :disabled="busy" @click="importRow(row)">
                        Import row
                      </button>
                      <button
                        type="button"
                        class="text-sm font-semibold text-blue-700 hover:underline"
                        :aria-pressed="editOf(row).skipped"
                        @click="setEdit(row, { skipped: !editOf(row).skipped })"
                      >
                        {{ editOf(row).skipped ? 'Keep row' : 'Skip row' }}
                      </button>
                      <button v-if="kept(row) && row.candidates" type="button" class="text-sm font-semibold text-blue-700 hover:underline" @click="splitCard(row)">
                        Split row
                      </button>
                    </template>
                  </span>
                </div>

                <ul v-if="kept(row) && rowDone(row)" class="m-0 flex list-none flex-col gap-0.5 p-0 text-xs text-gray-700">
                  <li v-for="l in draftLines(row, editOf(row))" :key="l.lineNo" class="tabular-nums">
                    Line {{ l.lineNo }} ·
                    <span class="font-mono">{{ l.partNumber ?? '—' }}</span>
                    · {{ fmtPcs(l.quantity) }} pcs
                  </li>
                </ul>
                <template v-else-if="kept(row)">
                  <div v-if="inputsOpen.has(row.rowKey)" class="grid gap-3 sm:grid-cols-3">
                    <label v-if="dateOpen.has(row.rowKey)" class="label flex flex-col gap-1">
                      {{ isShipped(row) ? 'Shipped on' : 'Planned ship date' }}
                      <input
                        :value="editOf(row).date"
                        type="date"
                        class="input h-8 !rounded"
                        :aria-describedby="problemId(row)"
                        @input="setEdit(row, { date: ($event.target as HTMLInputElement).value })"
                      />
                    </label>
                    <template v-if="isShipped(row)">
                      <label class="label flex flex-col gap-1">
                        Forwarder
                        <select
                          :value="editOf(row).forwarder"
                          class="select h-8 !rounded !py-1"
                          :aria-describedby="problemId(row)"
                          @change="setEdit(row, { forwarder: ($event.target as HTMLSelectElement).value })"
                        >
                          <option value="">Choose a forwarder</option>
                          <option v-for="f in forwarderOptions(row.forwarder)" :key="f" :value="f">{{ f }}</option>
                        </select>
                      </label>
                      <label class="label flex flex-col gap-1">
                        <span>
                          HAWB/BOL
                          <span class="font-normal text-gray-500">(optional)</span>
                        </span>
                        <input
                          :value="editOf(row).hawb"
                          class="input h-8 !rounded font-mono"
                          :maxlength="SHIPPING_TEXT_MAX"
                          @input="setEdit(row, { hawb: ($event.target as HTMLInputElement).value })"
                        />
                      </label>
                    </template>
                    <label class="label flex flex-col gap-1 sm:col-span-3">
                      <span>
                        Note
                        <span class="font-normal text-gray-500">(optional)</span>
                      </span>
                      <input
                        :value="editOf(row).note"
                        class="input h-8 !rounded"
                        :maxlength="NOTE_MAX"
                        :aria-describedby="problemId(row)"
                        @input="setEdit(row, { note: ($event.target as HTMLInputElement).value })"
                      />
                    </label>
                  </div>
                  <p v-else-if="editOf(row).note" class="m-0 text-xs text-gray-500">{{ editOf(row).note }}</p>

                  <p v-if="linesOpen.has(row.rowKey) && row.candidates && !hasPiecesLeft(free.get(row.rowKey) ?? new Map())" class="m-0 text-sm text-amber-800">
                    {{ noPiecesText(blockingRows(rows, edits, row.poNumber, row.rowKey)) }}
                  </p>
                  <LineTable
                    v-else-if="linesOpen.has(row.rowKey)"
                    :lines="lineOptions(row)"
                    :model-value="editOf(row).drafts"
                    :left="free.get(row.rowKey) ?? new Map()"
                    :error-field="null"
                    @update:model-value="setEdit(row, { drafts: $event })"
                  />
                  <div v-else-if="row.shipment || row.candidates" class="flex flex-wrap items-start gap-x-3">
                    <ul v-if="row.shipment" class="m-0 flex list-none flex-col gap-0.5 p-0 text-xs text-gray-700">
                      <li v-for="l in row.shipment.lines" :key="l.odooLineId" class="tabular-nums">
                        Line {{ l.lineNo }} ·
                        <span class="font-mono">{{ l.partNumber ?? '—' }}</span>
                        · {{ fmtPcs(l.quantity) }} pcs
                      </li>
                    </ul>
                    <button v-if="row.candidates" type="button" class="ml-auto text-xs font-semibold text-blue-700 hover:underline" @click="openLines(row)">Edit lines</button>
                  </div>
                </template>
              </li>
            </ul>
          </div>
        </section>
        <p v-else class="m-0 flex-1 self-center text-center text-sm text-gray-500">Pick a PO on the left.</p>
      </div>

      <footer class="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-5 py-3">
        <span v-if="footerAlert" class="mr-auto text-sm font-semibold text-red-700" role="alert">{{ footerAlert }}</span>
        <span v-else-if="lastSummary" class="mr-auto text-sm text-gray-600" role="status">{{ lastSummary }}</span>
        <template v-if="step === STEP.UPLOAD">
          <button type="button" class="btn-secondary" @click="requestClose">Cancel</button>
          <button type="button" class="btn-primary" :disabled="busy" @click="runPreview">Preview</button>
        </template>
        <template v-else>
          <button v-if="written.length > 0" type="button" class="btn-secondary" :disabled="busy" @click="requestClose">Done</button>
          <button v-else type="button" class="btn-secondary" @click="requestClose">Cancel</button>
          <button type="button" class="btn-primary" :disabled="busy" @click="importAll">Import all ready</button>
        </template>
      </footer>
      <ConfirmDialog
        :open="closeWarning !== null"
        title="Close the import?"
        :message="closeWarning ?? ''"
        confirm-label="Close"
        cancel-label="Go back"
        @confirm="closeNow"
        @cancel="closeWarning = null"
      />
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import LineTable from '@/components/poTracker/LineTable.vue';
  import PackingListInput from '@/components/poTracker/PackingListInput.vue';
  import { IMPORT_OUTCOME_HELP, IMPORT_OUTCOME_TONE } from '@/components/poTracker/poTrackerStyles';
  import { useFieldError } from '@/composables/useFieldError';
  import { previewImport, pushImport } from '@/services/poTrackerService';
  import { useCalendarPrivacyStore } from '@/store/calendarPrivacyStore';
  import { useCustomerMappingStore } from '@/store/customerMappingStore';
  import { IMPORT_OUTCOME, type ImportOutcome, type ImportPreview, type ImportPreviewRow, type ImportPushBody, type PoLineQuantities } from '@/types/poTracker';
  import { extractErrorMessage } from '@/utils/apiError';
  import { resolvePoSegmentColor } from '@/utils/customerColors';
  import { localIsoToday } from '@/utils/dates';
  import { fmtDay, fmtPcs, type FieldError } from '@/utils/poTracker';
  import {
    applyImportResult,
    closeWarningText,
    blockingRows,
    countByTab,
    draftLines,
    forwarderOptions,
    freePieces,
    hasPiecesLeft,
    hawbConflicts,
    IMPORT_TAB,
    importedPoNumbers,
    initialEdit,
    inputNeededText,
    isRowImported,
    isKept,
    isShipped,
    needsDateInput,
    noPiecesText,
    NOTE_MAX,
    nothingToImport,
    openRows,
    partlyImportedPos,
    failedReason,
    pendingForPo,
    pendingRows,
    picksLines,
    poSummaries,
    posInTab,
    pushRequestForPo,
    pushRequestForReady,
    pushRequestForRow,
    pushSummary,
    rowNote,
    rowProblem,
    SHIPPING_TEXT_MAX,
    splitNote,
    splitRow,
    tabSnapshot,
    type ImportTab,
    type RowImportResult,
    type PoSummary,
    type RowEdit,
  } from '@/utils/trackerImportPreview';

  // Schedule's dialog widths: the upload step is a narrow form, the preview the two-pane pair.
  const UPLOAD_WIDTH = 560;
  const PAIR_WIDTH = 1320;
  const LIST_WIDTH = '20rem';
  const SELECTED_BAR_MIX = '70%';

  const STEP = { UPLOAD: 'upload', PREVIEW: 'preview' } as const;
  // Square like every other surface in this dialog; the global badge is a pill for the tracker table.
  const BADGE = 'badge !rounded';
  type Step = (typeof STEP)[keyof typeof STEP];

  const OUTCOME_LABEL: Record<ImportOutcome, string> = {
    [IMPORT_OUTCOME.SKIPPED_UNKNOWN_PO]: 'Unknown PO',
    [IMPORT_OUTCOME.SKIPPED_HAS_SHIPMENTS]: 'Already on the tracker',
    [IMPORT_OUTCOME.NEEDS_DATE]: 'Needs a date',
    [IMPORT_OUTCOME.NEEDS_LINES]: 'Needs lines',
    [IMPORT_OUTCOME.OVER_ORDER]: 'Over the order',
    [IMPORT_OUTCOME.SHIPPED_MISSING]: 'Needs a forwarder',
    [IMPORT_OUTCOME.RESOLVED]: 'Ready',
  };

  const TABS: { tab: ImportTab; label: string; empty: string }[] = [
    { tab: IMPORT_TAB.READY, label: 'Ready', empty: 'No rows ready yet.' },
    { tab: IMPORT_TAB.NEEDS_INPUT, label: 'Needs input', empty: 'Nothing needs your input.' },
    { tab: IMPORT_TAB.SKIPPED, label: 'Skipped', empty: 'No rows skipped.' },
  ];

  const props = defineProps<{ open: boolean; vendorId: string }>();
  const emit = defineEmits<{ close: []; done: [poNumbers: string[]] }>();

  const root = ref<HTMLElement | null>(null);
  const step = ref<Step>(STEP.UPLOAD);
  const file = ref<File | null>(null);
  const preview = ref<ImportPreview | null>(null);
  // Replaced, never mutated, so the computeds below follow every edit.
  const edits = ref<Map<string, RowEdit>>(new Map());
  const results = ref<Map<string, RowImportResult>>(new Map());
  // Stay open once shown, so an input doesn't vanish while it's being filled in.
  const inputsOpen = ref<Set<string>>(new Set());
  const linesOpen = ref<Set<string>>(new Set());
  const dateOpen = ref<Set<string>>(new Set());
  const error = ref<FieldError | null>(null);
  // Which Import button was refused and why; the text stays live so it clears as rows are fixed.
  const poAlert = ref<{ poNumber: string; nothing: boolean } | null>(null);
  const rowAlert = ref<{ rowKey: string; nothing: boolean } | null>(null);
  const allTried = ref(false);
  const closeWarning = ref<string | null>(null);
  const lastSummary = ref<string | null>(null);
  const busy = ref(false);
  const tab = ref<ImportTab>(IMPORT_TAB.NEEDS_INPUT);
  const selected = ref<string | null>(null);
  // The list filters on this, the counts on the live problems: see tabSnapshot.
  const shownTabs = ref<Map<string, ImportTab>>(new Map());
  const today = ref(localIsoToday());

  const { errorId } = useFieldError(error);
  const mappingStore = useCustomerMappingStore();
  const privacyStore = useCalendarPrivacyStore();

  const rows = computed(() => preview.value?.rows ?? []);
  const rowsToImport = computed(() => openRows(rows.value, results.value));
  const tabCounts = computed(() => countByTab(rowsToImport.value, edits.value, problems.value));
  const summaries = computed(() => poSummaries(rows.value, edits.value, problems.value, results.value));
  const listedPos = computed(() => posInTab(rows.value, shownTabs.value, tab.value));
  const listedSummaries = computed(() => summaries.value.filter((s) => listedPos.value.includes(s.poNumber)));
  const selectedSummary = computed(() => summaries.value.find((s) => s.poNumber === selected.value) ?? null);
  const selectedRows = computed(() => rows.value.filter((r) => r.poNumber === selected.value));
  const written = computed(() => importedPoNumbers(rows.value, results.value));
  const footerAlert = computed(() => {
    if (error.value?.field === 'push') return error.value.message;
    return allTried.value && pendingKeys.value.length > 0 ? inputNeededText(pendingKeys.value.length) : null;
  });
  const poAlertText = computed(() => {
    const alert = poAlert.value;
    if (!alert || alert.poNumber !== selected.value) return null;
    if (alert.nothing) return 'Nothing to import';
    const left = pendingForPo(rows.value, pendingKeys.value, alert.poNumber).length;
    return left > 0 ? inputNeededText(left) : null;
  });
  // Indexed by the PO's place in the sheet, so a PO keeps its colour across tabs.
  const colors = computed(
    () =>
      new Map(
        [...new Set(rows.value.map((r) => r.poNumber))].map((poNumber, index) => [
          poNumber,
          resolvePoSegmentColor(poNumber, mappingStore.items, { hideCustomerMappings: privacyStore.hideCustomerMappings, index }),
        ])
      )
  );
  const free = computed(() => new Map(rows.value.map((r) => [r.rowKey, freePieces(rows.value, edits.value, r.poNumber, r.rowKey)])));
  // Over every row, imported ones included; only rows still to import get a problem.
  const conflicts = computed(() => hawbConflicts(rows.value, edits.value, results.value));
  const problems = computed(
    () =>
      new Map(
        rowsToImport.value
          .filter((r) => kept(r))
          .map((r) => [r.rowKey, rowProblem(r, editOf(r), free.value.get(r.rowKey) ?? new Map(), today.value, conflicts.value.get(r.rowKey) ?? null)])
      )
  );
  const pendingKeys = computed(() => pendingRows(rows.value, edits.value, today.value, results.value));

  function editOf(row: ImportPreviewRow): RowEdit {
    return edits.value.get(row.rowKey) ?? initialEdit(row);
  }

  function noteOf(row: ImportPreviewRow): string | null {
    return rowNote(row, editOf(row), problems.value.get(row.rowKey) ?? null);
  }

  function problemId(row: ImportPreviewRow): string | undefined {
    return problems.value.get(row.rowKey) ? errorId(`row-${row.rowKey}`) : undefined;
  }

  function setEdit(row: ImportPreviewRow, patch: Partial<RowEdit>): void {
    edits.value = new Map(edits.value).set(row.rowKey, { ...editOf(row), ...patch });
  }

  function kept(row: ImportPreviewRow): boolean {
    return isKept(row, editOf(row));
  }

  function pushable(row: ImportPreviewRow): boolean {
    return isKept(row, { ...editOf(row), skipped: false });
  }

  // The picked PO's bar: its customer colour, a shade darker.
  function darker(color: string | undefined): string | undefined {
    return color ? `color-mix(in srgb, ${color} ${SELECTED_BAR_MIX}, black)` : undefined;
  }

  function rowsLabel(n: number): string {
    return n === 1 ? '1 row' : `${n} rows`;
  }

  function rowDone(row: ImportPreviewRow): boolean {
    return isRowImported(results.value, row.rowKey);
  }

  // Live like the PO alert: the row's own problem until it's fixed.
  function rowAlertText(row: ImportPreviewRow): string | null {
    if (rowAlert.value?.rowKey !== row.rowKey) return null;
    return rowAlert.value.nothing ? 'Nothing to import' : (problems.value.get(row.rowKey) ?? null);
  }

  function summaryTone(s: PoSummary): string {
    if (s.failed !== null) return 'font-semibold text-red-700';
    if (s.done) return 'font-semibold text-green-700';
    return s.counts[IMPORT_TAB.NEEDS_INPUT] > 0 ? 'text-amber-800' : 'text-gray-500';
  }

  function lineOptions(row: ImportPreviewRow): PoLineQuantities[] {
    const left = free.value.get(row.rowKey) ?? new Map<number, number>();
    return (row.candidates ?? []).map((c) => ({
      odooLineId: c.odooLineId,
      lineNo: c.lineNo,
      partNumber: c.partNumber,
      description: c.description,
      ordered: c.ordered,
      shipped: 0,
      planned: 0,
      toPlan: left.get(c.odooLineId) ?? 0,
    }));
  }

  function openLines(row: ImportPreviewRow): void {
    linesOpen.value = new Set(linesOpen.value).add(row.rowKey);
  }

  // A new card under the row, every control open, for the partner to give it lines and pieces.
  function splitCard(row: ImportPreviewRow): void {
    if (!preview.value) return;
    const { rows: next, added } = splitRow(rows.value, row);
    preview.value = { ...preview.value, rows: next };
    edits.value = new Map(edits.value).set(added.rowKey, initialEdit(added));
    inputsOpen.value = new Set(inputsOpen.value).add(added.rowKey);
    linesOpen.value = new Set(linesOpen.value).add(added.rowKey);
    dateOpen.value = new Set(dateOpen.value).add(added.rowKey);
    void nextTick(() => root.value?.querySelector(`[data-import-row="${CSS.escape(added.rowKey)}"]`)?.scrollIntoView({ block: 'center' }));
  }

  // Picking a PO opens every row's line picker; an imported PO's rows stay read-only.
  watch(selected, (poNumber) => {
    if (!poNumber) return;
    const withCandidates = rows.value.filter((r) => r.poNumber === poNumber && r.candidates !== null && !rowDone(r)).map((r) => r.rowKey);
    linesOpen.value = new Set([...linesOpen.value, ...withCandidates]);
  });

  // Sticky like inputsOpen, so aligning a HAWB/BOL's date doesn't hide the input it was typed in.
  function dateRows(rowKeys: string[]): string[] {
    return rows.value.filter((r) => rowKeys.includes(r.rowKey) && (needsDateInput(r, today.value) || conflicts.value.has(r.rowKey))).map((r) => r.rowKey);
  }

  // Opens every control a pending row might need, then shows the first one.
  function revealPending(pending: string[]): void {
    inputsOpen.value = new Set([...inputsOpen.value, ...pending]);
    const withCandidates = rows.value.filter((r) => pending.includes(r.rowKey) && r.candidates !== null).map((r) => r.rowKey);
    linesOpen.value = new Set([...linesOpen.value, ...withCandidates]);
    dateOpen.value = new Set([...dateOpen.value, ...dateRows(pending)]);
    void nextTick(() => root.value?.querySelector(`[data-import-row="${CSS.escape(pending[0])}"]`)?.scrollIntoView({ block: 'center' }));
  }

  function retakeTabs(): void {
    shownTabs.value = tabSnapshot(rows.value, edits.value, problems.value);
  }

  function selectPo(poNumber: string): void {
    selected.value = poNumber;
  }

  // Keeps the picked PO when the new tab lists it, else picks the tab's first.
  function selectTab(next: ImportTab): void {
    tab.value = next;
    retakeTabs();
    if (!selected.value || !listedPos.value.includes(selected.value)) selected.value = listedPos.value[0] ?? null;
  }

  async function runPreview(): Promise<void> {
    if (busy.value) return;
    if (!file.value) {
      error.value = { field: 'file', message: 'Choose the sheet to import' };
      return;
    }
    error.value = null;
    busy.value = true;
    try {
      const next = await previewImport(props.vendorId, file.value);
      today.value = localIsoToday();
      edits.value = new Map(next.rows.map((r) => [r.rowKey, initialEdit(r)]));
      results.value = new Map();
      preview.value = next;
      // A resolved row can still break a push-time rule (future ATD, long forwarder, HAWB/BOL clash).
      const problemRows = next.rows.filter((r) => kept(r) && (r.outcome !== IMPORT_OUTCOME.RESOLVED || problems.value.get(r.rowKey))).map((r) => r.rowKey);
      inputsOpen.value = new Set(problemRows);
      dateOpen.value = new Set(dateRows(problemRows));
      linesOpen.value = new Set(next.rows.filter(picksLines).map((r) => r.rowKey));
      selectTab(pendingKeys.value.length > 0 ? IMPORT_TAB.NEEDS_INPUT : IMPORT_TAB.READY);
      step.value = STEP.PREVIEW;
    } catch (previewError) {
      error.value = { field: 'file', message: extractErrorMessage(previewError) };
    } finally {
      busy.value = false;
    }
  }

  // Shared by both buttons; a request that throws as a whole fails every PO it carried.
  async function send(request: ImportPushBody): Promise<void> {
    busy.value = true;
    try {
      const result = await pushImport(request);
      results.value = applyImportResult(results.value, rows.value, request, result);
      lastSummary.value = pushSummary(result);
    } catch (pushError) {
      const reason = extractErrorMessage(pushError);
      const poNumbers = [...new Set(request.shipments.map((s) => s.poNumber))];
      results.value = applyImportResult(results.value, rows.value, request, { written: [], failed: poNumbers.map((poNumber) => ({ poNumber, reason })) });
      lastSummary.value = null;
    } finally {
      busy.value = false;
      retakeTabs();
    }
  }

  function clearAlerts(): void {
    error.value = null;
    poAlert.value = null;
    rowAlert.value = null;
  }

  async function importRow(row: ImportPreviewRow): Promise<void> {
    if (busy.value) return;
    clearAlerts();
    if (problems.value.get(row.rowKey)) {
      rowAlert.value = { rowKey: row.rowKey, nothing: false };
      revealPending([row.rowKey]);
      return;
    }
    const request = pushRequestForRow(props.vendorId, rows.value, edits.value, results.value, row.rowKey);
    if (nothingToImport(request)) {
      rowAlert.value = { rowKey: row.rowKey, nothing: true };
      return;
    }
    await send(request);
  }

  async function importPo(poNumber: string): Promise<void> {
    if (busy.value) return;
    clearAlerts();
    const pending = pendingForPo(rows.value, pendingKeys.value, poNumber);
    if (pending.length > 0) {
      poAlert.value = { poNumber, nothing: false };
      revealPending(pending);
      return;
    }
    const request = pushRequestForPo(props.vendorId, rows.value, edits.value, results.value, poNumber);
    if (nothingToImport(request)) {
      poAlert.value = { poNumber, nothing: true };
      return;
    }
    await send(request);
  }

  // Imports every ready PO, then shows what still needs input.
  async function importAll(): Promise<void> {
    if (busy.value) return;
    clearAlerts();
    allTried.value = true;
    const request = pushRequestForReady(props.vendorId, rows.value, edits.value, pendingKeys.value, results.value);
    if (nothingToImport(request)) {
      error.value = pendingKeys.value.length > 0 ? null : { field: 'push', message: 'Nothing is ready to import' };
    } else {
      await send(request);
    }
    const pending = pendingKeys.value;
    if (pending.length === 0) return;
    selectTab(IMPORT_TAB.NEEDS_INPUT);
    selected.value = rows.value.find((r) => r.rowKey === pending[0])?.poNumber ?? selected.value;
    revealPending(pending);
  }

  // The dialog is persistent (a stray click or Esc would drop every edit): only Cancel and Done close it.
  // Ignored while a call runs: a push closed mid-flight would never refresh what it wrote.
  // A PO left half imported asks first: a later import of the sheet skips it.
  function requestClose(): void {
    if (busy.value) return;
    closeWarning.value = closeWarningText(partlyImportedPos(rows.value, edits.value, results.value));
    if (closeWarning.value === null) closeNow();
  }

  function closeNow(): void {
    closeWarning.value = null;
    if (written.value.length > 0) emit('done', written.value);
    emit('close');
  }
</script>
